import {useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {Modal} from 'react-bootstrap'
import {defaultCreateAppData, ICreateAppData} from './IAppModels'
import {StepperComponent} from '../../../assets/ts/components'
import {KTIcon} from '../../../helpers'
import {Step1} from './steps/Step1'
import {Step2} from './steps/Step2'
import {Step3} from './steps/Step3'
import {Step4} from './steps/Step4'
import {Step5} from './steps/Step5'
import {useNavigate} from 'react-router-dom'
import {ToastContainer, toast} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {useAuth} from '../../../../app/modules/auth'
import {IPatientData} from '../../interface/IPatientData'
import moment from 'moment'
import {IoClose} from 'react-icons/io5'
import {FaArrowRight, FaArrowLeft} from 'react-icons/fa'
import {useIntl} from 'react-intl'
import {useQueryClient} from 'react-query'

type Props = {
  show: boolean
  handleClose: () => void
}

const modalsRoot = document.getElementById('root-modals') || document.body

const CreateAppModal = ({show, handleClose}: Props) => {
  const user = useAuth()
  const {formatMessage: f} = useIntl()
  const {auth} = useAuth()
  const queryClient = useQueryClient()
  const stepperRef = useRef<HTMLDivElement | null>(null)
  const stepper = useRef<StepperComponent | null>(null)
  const [data, setData] = useState<ICreateAppData>(defaultCreateAppData)
  const [hasError, setHasError] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [curStep, setCurStep] = useState(0)

  const [patientData, setPatientData] = useState<IPatientData | null>(null)

  // State Manage Each Validation Stepper
  const [globalValidation, setGlobalValidation] = useState(false) //global validation
  const [isPatientDataFetched, setIsPatientDataFetched] = useState(false) //step 1
  const [isFormulirDataFetched, setIsFormulirDataFetched] = useState(false) //step 2
  const [isKetentuanDataFetched, setIsKetentuanDataFetched] = useState(false) //step 3
  const [isSelesaiDataFetched, setIsSelesaiDataFetched] = useState(false) //step 4
  const [isClaimValidation, setIsClaimValidation] = useState(false) // step 1 after isPatientDataFetched

  const [exclusionMessage, setExclusionMessage] = useState('')
  const [exclusionMessageBenefit, setExclusionMessageBenefit] = useState('')

  const [isStep2Valid, setIsStep2Valid] = useState(false)
  const [isStep3Valid, setIsStep3Valid] = useState(false)
  const [pdlWarningMessage, setPdlWarningMessage] = useState('')
  const [isStep2ButtonEnabled, setIsStep2ButtonEnabled] = useState(false)

  // Each open of the modal is a session. The callbacks below capture the session they were
  // rendered in, so a lookup or validation response that lands after the modal closed (or during
  // its close animation, while Step1 is still mounted) cannot write the previous member back into
  // the reopened modal and enable Next for them.
  const sessionRef = useRef(0)
  const session = sessionRef.current
  const isCurrentSession = () => session === sessionRef.current

  // Reset data when modal is closed
  useEffect(() => {
    if (!show) {
      sessionRef.current += 1 // responses from the closed session become no-ops
      setData(defaultCreateAppData) // Reset to initial data
      setHasError(false)
      setCurrentStep(0)
      setCurStep(0)
      setPatientData(null)
      setIsPatientDataFetched(false)
      setIsClaimValidation(false)
      setIsFormulirDataFetched(false)
      // setIsKetentuanDataFetched(false);
      setIsSelesaiDataFetched(false)
      setIsStep2Valid(false)
      setPdlWarningMessage('')
      setIsStep2ButtonEnabled(false)
      // setIsStep3Valid(false);
    }
  }, [show])

  // Resolve the stepper at click time rather than relying only on the modal's onEntered:
  // if onEntered has not run, the instance is missing (or bound to a previous modal's DOM)
  // and Next would silently do nothing.
  const getStepper = () => {
    const element = stepperRef.current
    if (!element) {
      return null
    }
    if (stepper.current?.element !== element) {
      stepper.current = StepperComponent.createInsance(element)
    }
    return stepper.current
  }

  // react-bootstrap keeps the modal body mounted until the close transition ends. Reopening in
  // that window reuses the same stepper element and instance, still at its old step, while the
  // close-reset effect has already put React back on the first step. Start every open on step 1.
  const loadStepper = () => {
    getStepper()?.goFirst()
  }

  const updateData = (fieldsToUpdate: Partial<ICreateAppData>) => {
    if (!isCurrentSession()) return
    const updatedData = {...data, ...fieldsToUpdate}
    setData(updatedData)
  }

  const prevStep = () => {
    const currentStepper = getStepper()
    if (!currentStepper) {
      console.warn('[ClaimRegistration] Stepper is not initialised; cannot go to the previous step')
      return
    }
    currentStepper.goPrev()
    setCurrentStep(currentStepper.currentStepIndex || 0)
  }

  const nextStep = async () => {
    if (currentStep == 2) {
      const isValid = await validateExclusionProduct()
      if (!isValid) {
        return
      } else {
        setExclusionMessage('')
        setExclusionMessageBenefit('')
      }
    }
    setHasError(false)
    const currentStepper = getStepper()
    if (!currentStepper) {
      console.warn('[ClaimRegistration] Stepper is not initialised; cannot go to the next step')
      return
    }

    currentStepper.goNext()
    setCurrentStep(currentStepper.currentStepIndex || 0)
  }

  const handlePatientDataFetchSuccess = (fetchedPatientData: IPatientData) => {
    if (!isCurrentSession()) return
    setIsPatientDataFetched(true)
    setPatientData(fetchedPatientData)
  }

  const handleClaimValidationSuccess = (isValid: boolean) => {
    if (!isCurrentSession()) return
    setIsClaimValidation(isValid)
  }

  const handlePatientDataFetchError = () => {
    if (!isCurrentSession()) return
    setIsPatientDataFetched(false)
    setPatientData(null)
  }

  const handleStep2ValidationSuccess = () => {
    setIsStep2Valid(true)
  }

  const handleStep2ValidationError = () => {
    setIsStep2Valid(false)
  }

  const handleStep3ValidationSuccess = () => {
    setIsStep3Valid(true)
  }

  const handleStep3ValidationError = () => {
    setIsStep3Valid(false)
  }

  // Effect to handle PDL Step 2 validation
  useEffect(() => {
    const isPDLInsurer = data.appBasic.memberData?.insurerCode === 'PDL'
    const isSpecialProvider = user.currentUser?.providerCode === '0224000839'
    const isTelemedicineBenefit = data.appBasic.benefitDetail?.benefitName === 'TELEMEDICINE'
    const isRawatJalan = data.appBasic.treatmentType === 'Rawat jalan'
    const isPhoneNumberValid = data.appBasic.phoneNumber && data.appBasic.phoneNumber.trim() !== ''

    // Clear previous warning
    setPdlWarningMessage('')

    // Check phone number first - required for all cases
    if (!isPhoneNumberValid) {
      setIsStep2ButtonEnabled(false)
      return
    }

    if (isPDLInsurer) {
      if (isRawatJalan) {
        if (isSpecialProvider && isTelemedicineBenefit) {
          // Allow: PDL + special provider + TELEMEDICINE + Rawat jalan + phone number valid
          setIsStep2ButtonEnabled(isStep2Valid)
        } else if (isSpecialProvider) {
          // Disable: PDL + special provider + non-TELEMEDICINE + Rawat jalan
          // setPdlWarningMessage(
          //   'Untuk PDL dengan provider GOOD DOCTOR INDONESIA, Rawat jalan hanya diperbolehkan untuk benefit TELEMEDICINE'
          // )
          setPdlWarningMessage(f({id: 'CLAIM.REGISTER.PDL_OUTPATIENT_VALIDATION'}))
          setIsStep2ButtonEnabled(false)
        } else {
          // Disable: PDL + any other provider + Rawat jalan
          // setPdlWarningMessage('Untuk PDL, Rawat jalan tidak diperbolehkan')
          setPdlWarningMessage(f({id: 'CLAIM.REGISTER.OTHER_PDL_OUTPATIENT_VALIDATION'}))
          setIsStep2ButtonEnabled(false)
        }
      } else {
        // For PDL + non-Rawat jalan cases, use standard validation
        setIsStep2ButtonEnabled(isStep2Valid)
      }
    } else {
      // For all other cases, use the standard Step 2 validation
      setIsStep2ButtonEnabled(isStep2Valid)
    }
  }, [
    data.appBasic.memberData?.insurerCode,
    user.currentUser?.providerCode,
    data.appBasic.benefitDetail?.benefitName,
    data.appBasic.treatmentType,
    data.appBasic.phoneNumber,
    isStep2Valid,
  ])

  const submit = async () => {
    const currentDate = moment().format('YYYY-MM-DDTHH:mm:ss.SSS')
    const requestBody = {
      id: 0,
      providerCode: user.currentUser?.providerCode,
      providerName: user.currentUser?.providerName,
      providerAddress: user.currentUser?.providerAddress,
      providerCity: user.currentUser?.providerCity,
      claimDate: currentDate,
      polyCode: '',
      polyName: '',
      doctor: '',
      phone: data.appBasic.phoneNumber,
      claimSource: 'CASHLESS',
      claimType: 'Web Provider',
      statusClaim: 'Registered',
      stPost: false,
      stPaid: false,
      stExgratia: false,
      stHighPlan: false,
      createdBy: user.currentUser?.email,
      idBenefit: data.appBasic.benefitDetail.id,
      benefitCode: data.appBasic.benefitDetail.benefitCode,
      benefitName: data.appBasic.benefitDetail.benefitName,
      member: {
        memberId: patientData?.memberId,
        memberName: patientData?.memberName,
        cardNumber: patientData?.cardNumber,
        nik: patientData?.nik,
        dateOfBirth: patientData?.dateOfBirth,
        gender: patientData?.gender,
        maritalStatus: patientData?.maritalStatus,
        memberType: patientData?.memberType,
        memberStatus: patientData?.memberStatus,
        isSuspended: patientData?.isSuspended,
        isVip: patientData?.isVip,
        isAso: patientData?.isAso,
        isIndividual: patientData?.isIndividual,
        joinDate: patientData?.joinDate,
        policyNumber: patientData?.policyNo,
        policyStart: patientData?.policyStart,
        policyEnd: patientData?.policyEnd,
        planCode:
          data.appBasic.benefitDetail.planCode ||
          data.appBasic.benefitDetail.planName ||
          data.appBasic.plan,
        planName: data.appBasic.benefitDetail.planName || data.appBasic.plan,
        insurerCode: patientData?.insurerCode,
        insurerName: patientData?.insurerName,
        remark: patientData?.remark,
        customerCode:
          patientData?.type === 'aso' || patientData?.type === 'group insurance'
            ? patientData?.corporateCode
            : patientData?.insurerCode,
        customerName:
          patientData?.type === 'aso' || patientData?.type === 'group insurance'
            ? patientData?.corporateName
            : patientData?.insurerName,
        memberInsuranceNumber: patientData?.memberInsuranceNumber,
        division: patientData?.division || '',
      },
      benefitDetail: data.appBasic.benefitDetail,
      treatmentType: data.appBasic.treatmentType,
      indate: data.appBasic.indate,
    }

    setLoading(true)
    try {
      // if (!process.env.REACT_APP_API_CLAIM_URL) {
      //   throw new Error("API URL is not defined");
      // }
      const response = await fetch(
        `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/Provider/CreateClaim`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization:
              `Bearer ${auth?.token}` ||
              `Bearer ${user.currentUser?.token}` ||
              `Bearer ${user.currentUser?.token}`,
          },
          body: JSON.stringify(requestBody),
        }
      )

      console.log('response', response)

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      const result = await response.blob()

      // Extract filename from Content-Disposition header (set by backend)
      // On staging/production (same origin), this will work
      // On local development (CORS), this may not be accessible
      const contentDisposition = response.headers.get('content-disposition')
      let filename: string

      if (contentDisposition) {
        // Use filename from backend
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition)
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '')
        } else {
          // Fallback if parsing fails
          filename = `Register_Form_${Date.now()}.pdf`
        }
      } else {
        // Fallback for local development (CORS blocks Content-Disposition header)
        filename = `Register_Form_${Date.now()}.pdf`
      }

      const url = window.URL.createObjectURL(result)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()

      toast.success('Submission Successful!')

      // Invalidate and refetch claims queries to update the UI
      queryClient.invalidateQueries(['claims'])
      queryClient.invalidateQueries(['myTurnTotal'])

      handleClose()
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  const getModalClass = () => {
    return currentStep === 3
      ? 'modal-dialog modal-dialog-centered modal-fullwidth'
      : 'modal-dialog modal-dialog-centered mw-900px'
  }

  const validateExclusionProduct = async () => {
    if (!patientData) return false

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_USER_URL}/api/Data/Provider/ValidateProviderExclusion`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.token || user.currentUser?.token}`,
          },
          body: JSON.stringify({
            providerCode: user.currentUser?.providerCode,
            benefitCode: data.appBasic.benefitDetail.benefitCode,
            planCode: data.appBasic.benefitDetail.planCode,
            productCode: patientData?.productCode,
          }),
        }
      )

      // First try to get the response as text
      const responseText = await response.text()

      let errorResult
      try {
        // Try to parse as JSON
        errorResult = JSON.parse(responseText)
      } catch (parseError) {
        // If it's not valid JSON, use the text as is
        console.error('Failed to parse response as JSON:', parseError)
        errorResult = {message: responseText}
      }

      // Now check if response was successful
      if (!response.ok) {
        console.log('Error result:', errorResult)

        const errorMessage =
          errorResult.message || errorResult.Message || errorResult.error || 'Unknown error'

        if (errorMessage.includes('Exclusion Product')) {
          // setExclusionMessage(
          //   'Member dengan Product ini tidak dapat melakukan klaim pada rumah sakit ini'
          // )
          setExclusionMessage(f({id: 'CLAIM.REGISTER.EXCLUSION_PRODUCT_VALIDATION'}))
          setExclusionMessageBenefit('')
        } else if (errorMessage.includes('Exclusion Sub-Product')) {
          // setExclusionMessage(
          //   'Member dengan Plan ini tidak dapat melakukan klaim pada rumah sakit ini'
          // )
          setExclusionMessage(f({id: 'CLAIM.REGISTER.EXCLUSION_SUB_PRODUCT_VALIDATION'}))
          setExclusionMessageBenefit('')
        } else if (errorMessage.includes('Exclusion Benefit')) {
          // setExclusionMessageBenefit(
          //   'Member dengan Manfaat ini tidak dapat melakukan klaim pada rumah sakit ini'
          // )
          setExclusionMessageBenefit(f({id: 'CLAIM.REGISTER.EXCLUSION_BENEFIT_VALIDATION'}))
          setExclusionMessage('')
        } else {
          // Handle any other error messages from the server
          setExclusionMessage(errorMessage || 'Terjadi kesalahan saat validasi. Silakan coba lagi.')
          setExclusionMessageBenefit('')
        }
        return false
      }

      // If we got here, response was successful
      return true
    } catch (error) {
      setExclusionMessage('Terjadi kesalahan saat menghubungi server. Periksa koneksi Anda.')
      setExclusionMessageBenefit('')
      return false
    }
  }

  return createPortal(
    <>
      <Modal
        data-testid='create-app-modal'
        id='kt_modal_create_app'
        tabIndex={-1}
        aria-hidden='true'
        dialogClassName={getModalClass()}
        show={show}
        onHide={handleClose}
        onEntered={loadStepper}
        backdrop={true}
        style={currentStep === 3 ? {maxWidth: '75vw', marginLeft: '12.5%'} : {}}
      >
        <div
          className='modal-header'
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{margin: 0}}>{f({id: 'CLAIM.REGISTER.MODAL_TITLE'})}</h2>
          <div
            className='btn btn-sm btn-icon btn-active-color-primary'
            onClick={handleClose}
            style={{
              marginLeft: 'auto', // This will push the button to the right
              cursor: 'pointer',
            }}
          >
            <IoClose className='fs-1' />
          </div>
        </div>

        <div className='modal-body py-lg-10 px-lg-10'>
          <div
            ref={stepperRef}
            className='stepper stepper-pills stepper-column d-flex flex-column flex-xl-row flex-row-fluid'
            id='kt_modal_create_app_stepper'
          >
            <div className='d-flex justify-content-center justify-content-xl-start flex-row-auto w-100 w-xl-300px'>
              <div className='stepper-nav ps-lg-10'>
                {/* Step Navigation */}
                <div className='stepper-item current' data-kt-stepper-element='nav'>
                  <div className='stepper-wrapper'>
                    <div className='stepper-icon w-40px h-40px'>
                      <i className='stepper-check fas fa-check'></i>
                      <span className='stepper-number'>1</span>
                    </div>
                    <div className='stepper-label'>
                      <h3 className='stepper-title'>
                        {f({id: 'CLAIM.REGISTER.STEP_IDENTIFICATION'})}
                      </h3>
                      <div className='stepper-desc'>
                        {f({id: 'CLAIM.REGISTER.STEP_PATIENT_DATA'})}
                      </div>
                    </div>
                  </div>
                  <div className='stepper-line h-40px'></div>
                </div>

                <div className='stepper-item' data-kt-stepper-element='nav'>
                  <div className='stepper-wrapper'>
                    <div className='stepper-icon w-40px h-40px'>
                      <i className='stepper-check fas fa-check'></i>
                      <span className='stepper-number'>2</span>
                    </div>
                    <div className='stepper-label'>
                      <h3 className='stepper-title'>{f({id: 'CLAIM.REGISTER.STEP_FORMULIR'})}</h3>
                      <div className='stepper-desc'>
                        {f({id: 'CLAIM.REGISTER.STEP_FORMULIR_DESCRIPTION'})}
                      </div>
                    </div>
                  </div>
                  <div className='stepper-line h-40px'></div>
                </div>

                {/* <div className="stepper-item" data-kt-stepper-element="nav">
                  <div className="stepper-wrapper">
                    <div className="stepper-icon w-40px h-40px">
                      <i className="stepper-check fas fa-check"></i>
                      <span className="stepper-number">3</span>
                    </div>
                    <div className="stepper-label">
                      <h3 className="stepper-title">Ketentuan</h3>
                      <div className="stepper-desc">Ketentuan</div>
                    </div>
                  </div>
                  <div className="stepper-line h-40px"></div>
                </div> */}

                <div className='stepper-item' data-kt-stepper-element='nav'>
                  <div className='stepper-wrapper'>
                    <div className='stepper-icon w-40px h-40px'>
                      <i className='stepper-check fas fa-check'></i>
                      <span className='stepper-number'>3</span>
                    </div>
                    <div className='stepper-label'>
                      <h3 className='stepper-title'>{f({id: 'CLAIM.REGISTER.STEP_FINISH'})}</h3>
                      <div className='stepper-desc'>
                        {f({id: 'CLAIM.REGISTER.STEP_FINISH_DESCRIPTION'})}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='flex-row-fluid py-lg-5 px-lg-15'>
              <form noValidate id='kt_modal_create_app_form'>
                <Step1
                  data={data}
                  updateData={updateData}
                  hasError={hasError}
                  handlePatientDataFetchSuccess={handlePatientDataFetchSuccess}
                  handlePatientDataFetchError={handlePatientDataFetchError}
                  handleClaimValidationSuccess={handleClaimValidationSuccess}
                />
                <Step2
                  data={data}
                  updateData={updateData}
                  hasError={hasError}
                  handleStep2ValidationSuccess={handleStep2ValidationSuccess}
                  handleStep2ValidationError={handleStep2ValidationError}
                  errorMessageProduct={exclusionMessage}
                  errorMessageBenefit={exclusionMessageBenefit}
                />
                {/* <Step3
                  data={data}
                  updateData={updateData}
                  hasError={hasError}
                  handleStep3ValidationSuccess={handleStep3ValidationSuccess}
                  handleStep3ValidationError={handleStep3ValidationError}
                /> */}
                <Step4 data={data} updateData={updateData} hasError={hasError} />
                <Step5 />

                {/* Spinner for loading state */}
                <div
                  className='spinner spinner-primary'
                  id='loadingSpinner'
                  style={{display: 'none'}}
                >
                  <span className='spinner-border' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </span>
                </div>

                {pdlWarningMessage && currentStep === 2 && (
                  <div className='mt-3 alert alert-warning d-flex align-items-center'>
                    <KTIcon iconName='warning-2' className='fs-3 me-2' />
                    <span>{pdlWarningMessage}</span>
                  </div>
                )}

                <div className='d-flex flex-stack pt-10'>
                  <div className='me-2'>
                    <button
                      type='button'
                      className='btn btn-lg btn-light-primary me-3'
                      data-kt-stepper-action='previous'
                      onClick={prevStep}
                    >
                      <FaArrowLeft className='me-1' /> {f({id: 'CLAIM.REGISTER.STEP_BACK'})}
                    </button>
                  </div>
                  <div>
                    {/* The stepper is 1-based while currentStep starts at 0, so both 0 (fresh open)
                        and 1 (after Back) are the identification step and share one gate. */}
                    {(currentStep === 0 || currentStep === 1) && (
                      <button
                        type='button'
                        className='btn btn-lg btn-primary'
                        data-kt-stepper-action='next'
                        onClick={nextStep}
                        disabled={!isPatientDataFetched || !isClaimValidation}
                      >
                        {f({id: 'CLAIM.REGISTER.STEP_NEXT'})} <FaArrowRight className='ms-1 me-0' />
                      </button>
                    )}
                    {currentStep === 2 && (
                      <>
                        <button
                          type='button'
                          className='btn btn-lg btn-primary'
                          data-kt-stepper-action='next'
                          onClick={nextStep}
                          disabled={!isStep2ButtonEnabled}
                        >
                          {f({id: 'CLAIM.REGISTER.STEP_NEXT'})}
                          <FaArrowRight className='ms-1 me-0' />
                        </button>
                      </>
                    )}
                    {/* {currentStep === 3 && (
                      <button
                        type="button"
                        className="btn btn-lg btn-primary"
                        data-kt-stepper-action="next"
                        onClick={nextStep}
                        disabled={!data.appBasic.agreeToTerms}
                      >
                        Lanjut{" "}
                        <KTIcon
                          iconName="arrow-right"
                          className="fs-3 ms-1 me-0"
                        />
                      </button>
                    )} */}
                    {currentStep === 3 && (
                      <button
                        type='button'
                        className='btn btn-lg btn-primary'
                        data-kt-stepper-action='submit'
                        onClick={submit}
                        disabled={loading}
                      >
                        {loading
                          ? f({id: 'CLAIM.REGISTER.STEP_LOADING'})
                          : f({id: 'CLAIM.BUTTON.REGISTER'})}
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Modal>
      <ToastContainer />
      <style>
        {`
          .modal-fullwidth {
            width: 75vw;
            max-width: 75vw !important;
            margin: 1.75rem auto !important;
            left: 50% !important;
            transform: translateX(-50%) !important;
          }
          
          .modal-fullwidth .modal-content {
            margin: 0 auto;
            width: 100%;
            overflow-y: auto;
            max-height: 85vh;
          }

          .modal-body {
            overflow: hidden;
          }
        `}
      </style>
    </>,
    modalsRoot
  )
}

export {CreateAppModal}
